<template>
  <v-container class="checklist-container" fluid>
    <h4 class="checklist-view-title">Readiness Checklist</h4>
    <div class="d-flex gap-2 flex-row align-center inspection-badge-container">
      <ReadinessBadge :level="proficiencyLevelLabel" dot="Good Standing" view="ReadOnly" />
      <InspectionIssueBadge
        :currentBadgeLevel="
          badge && typeof badge.level === 'string' ? badge.level.toLowerCase() : 'novice'
        "
        :currentBadgeStatus="
          badge && typeof badge.issued_method === 'string'
            ? getBadgeStatusForInspection(badge.issued_method)
            : 'null'
        "
        :checkpoints="checkPoints ? checkPoints : []"
        :useCountsVariant="true"
        :checkpointCounts="checkpointCounts"
      />
      <ResAssignmentWithPath
        class="ml-5"
        resPathType="responsibility"
        :responsibilityCode="responsibility.code"
        :responsibilityName="responsibility.name"
      />
    </div>
    <div class="mx-5">
      <CatalogsMenu :items="menuItems" :value="selectedItem" @onChange="changeTab($event)" />
    </div>
    <v-tabs-items v-model="selectedTab" class="custom-tabs">
      <v-tab-item v-for="(item, index) in menuItems" :key="index" :value="index">
        <v-card elevation="0" v-if="index === 0">
          <UniversalTable
            :config="tableConfig.tableParams.value"
            :items="filteredMasteredResponsibilities"
            @onSearch="viewState.state.filters.searchText = $event"
            @onClick="viewState.state.filters.showFilterDialog = true"
            @removeFilter="viewState.removeFilters"
            @removeAllFilters="clearFilters"
            :emptyObjectData="emptyObjectData"
            :removeCounterLevelFilter="removeCounterLevelFilter"
            @levelCounterFilter="levelCounterFilter"
          >
          </UniversalTable>
          <SearchModal
            :dialog="viewState.state.filters.showFilterDialog"
            @back="viewState.state.filters.showFilterDialog = false"
            @checkedItemsChanged="viewState.applyFilters"
            :clearSelection="viewState.state.filters.clearSelection"
            filterType="Checklist"
            :filterGroups="viewState.state.filters.filterGroups"
            :statusOptions="[]"
            :modalWidth="800"
            :defaultSelected="viewState.state.filters.values"
          />
        </v-card>
        <v-card elevation="0" v-if="index === 1" class="ml-2">
          <ResponsibilityAvailableInspectorsCards :availableInspectors="availableInspectors" />
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script setup>
import { computed, ref, defineProps, onMounted, watch } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig.js";
import { useViewState } from "./composables/useReadinessChecklistViewState.js";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import CatalogsMenu from "@components/CatalogsMenu/CatalogsMenu.vue";
import InspectionIssueBadge from "@components/InspectionIssueBadge/InspectionIssueBadge.vue";
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";
import ResAssignmentWithPath from "@components/ResAssignmentWithPath/ResAssignmentWithPath.vue";
import ResponsibilityAvailableInspectorsCards from "@components/ResponsibilityAvailableInspectorsCards/ResponsibilityAvailableInspectorsCards.vue";
import { getListActiveCheckpointsEval } from "@/api";

const props = defineProps({
  proficiencyLevel: {
    type: Number,
    default: 0,
  },
  badge: {
    type: Object,
    default: () => ({}),
  },
  responsibility: {
    type: Object,
    default: () => ({}),
  },
  checkPoints: {
    type: Array,
    default: () => [],
  },
  checkpointCounts: {
    type: Object,
    default: () => ({}),
  },
  availableInspectors: {
    type: Array,
    default: () => [],
  },
  selectedItem: {
    type: Number,
    default: null,
  },
});

const tableConfig = useTableConfig();
const viewState = useViewState();
const emptyObjectData = {
  singular: "Checklist Item",
  plural: "Checklist Items",
};

const menuItems = [
  { title: "Checklist" },
  { title: "Available Inspectors", count: props?.availableInspectors?.length },
];

const selectedTab = ref(props?.selectedItem);

watch(
  () => props.selectedItem,
  (newVal) => {
    selectedTab.value = newVal;
  }
);

const dataStream = ref([]);
const levelFilter = ref([]); // Define levelFilter as a ref
const removeCounterLevelFilter = ref(false);

const filteredMasteredResponsibilities = computed(() => {
  let allMasteredResponsibilities = dataStream.value;

  if (viewState.state.filters.searchText && viewState.state.filters.searchText.length > 0) {
    const searchText = viewState.state.filters.searchText.toLowerCase().trim();
    // Split search text by spaces to handle multiple search terms
    const searchTerms = searchText.split(/\s+/).filter((term) => term.length > 0);

    allMasteredResponsibilities = allMasteredResponsibilities.filter((el) => {
      const criteria = el.evaluation_instruction.criteria.toLowerCase();
      const instructions = el.evaluation_instruction.instructions.toLowerCase();
      const combinedText = `${criteria} ${instructions}`;

      // Check if all search terms are present in the combined text
      return searchTerms.every((term) => combinedText.includes(term));
    });
  }

  const filterOptions = [
    "Not Yet Passed",
    "Only Failed",
    "Only Not Yet Evaluated",
    "Only Reset",
    "Only Not Applicable",
    "Only Passed",
  ];

  const checkEvaluationStatus = (item, filter) => {
    const evaluation_status = item.check_point.checkpoint_status;
    switch (filter) {
      case "Not Yet Passed":
        return (
          evaluation_status === null ||
          ["reset", "failed"].some((status) => evaluation_status?.includes(status))
        );
      case "Only Failed":
        return evaluation_status === "Failed";
      case "Only Not Yet Evaluated":
        return evaluation_status === "Not yet evaluated";
      case "Only Passed":
        return evaluation_status === "Passed";
      case "Only Not Applicable":
        return evaluation_status === "N/A";
      case "Only Reset":
        return evaluation_status === "Reset";
      default:
        return false;
    }
  };

  if (viewState.state.filters.values.some((val) => filterOptions.includes(val))) {
    allMasteredResponsibilities = allMasteredResponsibilities.filter((responsibility) => {
      return viewState.state.filters.values.some((filter) =>
        checkEvaluationStatus(responsibility, filter)
      );
    });
  }

  if (levelFilter.value.length > 0) {
    const levels = levelFilter.value.map((level) => getProficiencyLevelFromString(level));
    allMasteredResponsibilities = allMasteredResponsibilities.filter((item) =>
      levels.includes(item.evaluation_instruction.level)
    );
  }

  allMasteredResponsibilities = allMasteredResponsibilities.sort((a, b) => {
    const firstNameA = a?.name?.toLowerCase() || "";
    const firstNameB = b?.name?.toLowerCase() || "";
    const lastNameA = a?.code?.toLowerCase() || "";
    const lastNameB = b?.code?.toLowerCase() || "";
    if (firstNameA < firstNameB) return -1;
    if (firstNameA > firstNameB) return 1;
    if (lastNameA < lastNameB) return -1;
    if (lastNameA > lastNameB) return 1;
    return 0;
  });
  return allMasteredResponsibilities;
});

const getProficiencyLevel = (level) => {
  switch (level) {
    case 0:
      return "novice";
    case 1:
      return "apprentice";
    case 2:
      return "professional";
    case 3:
      return "coach";
    case 4:
      return "master";
    default:
      return "novice";
  }
};

const getProficiencyLevelFromString = (level) => {
  switch (level.toLowerCase()) {
    case "novice":
      return 0;
    case "apprentice":
      return 1;
    case "professional":
      return 2;
    case "coach":
      return 3;
    case "master":
      return 4;
    default:
      return 0;
  }
};

const levelCounter = computed(() => {
  return filteredMasteredResponsibilities.value.reduce((acc, item) => {
    const level = getProficiencyLevel(item?.evaluation_instruction?.level);
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {});
});

watch(
  levelCounter,
  (newVal) => {
    tableConfig.tableParams.value.levelCounter = newVal;
  },
  { immediate: true, deep: true }
);

const changeTab = (tab) => {
  selectedTab.value = tab;
};

onMounted(async () => {
  const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
  const user = JSON.parse(localStorage.getItem("user"));
  const employeeId = user?.employee?.id;

  const updateChangeEvaluationStatus = (data) => {
    if (data.evaluation_status == "not_applicable") {
      return "NA";
    } else if (data.evaluation_status == "passed") {
      return "Passed";
    } else if (data.evaluation_status == "failed") {
      return "Failed";
    } else {
      return "Not yet evaluated";
    }
  };

  const data3 = await getListActiveCheckpointsEval(accountID, props.responsibility.id, employeeId);

  if (data3 && data3.data) {
    dataStream.value = data3.data.map((el) => {
      return {
        id: el.id,
        check_point: {
          inspected_by_avatar: el.updated_by.picture,
          inspected_by: el.updated_by?.name || "",
          inspected_on: el.updated_on || null,
          checkpoint_status: updateChangeEvaluationStatus(el) || "",
        },
        evaluation_instruction: {
          rowId: el.id,
          status: el.status || "",
          level: el.proficiency_level || "",
          criteria: el.criteria || "",
          instructions: el.instructions || "",
          evaluation_by: el.updated_by || "",
          evaluation_on: el.updated_on || "",
          evaluation_reason: el.evaluation_reason || "",
        },
      };
    });
  }

  tableConfig.tableParams.value.levelCounter = levelCounter.value;
});

const levelCounterFilter = (event) => {
  levelFilter.value = event;
  tableConfig.tableParams.value.levelFilterCounter = event;
  if (removeCounterLevelFilter.value) {
    removeCounterLevelFilter.value = false;
  }
};

const clearFilters = () => {
  viewState.clearFilters();
  levelFilter.value = [];
  tableConfig.tableParams.value.levelFilterCounter = [];
  removeCounterLevelFilter.value = true;
};
const getBadgeStatusForInspection = (status) => {
  // Map the badge status to what InspectionIssueBadge component expects
  if (status === "given") {
    return "forced";
  } else if (status === "good_standing") {
    return "good_standing";
  } else if (status === "awarded") {
    return "good_standing"; // Map awarded to good_standing
  } else if (status === "obsolete") {
    return "obsolete";
  } else {
    return "null"; // default fallback
  }
};
</script>
<style scoped>
.icon-container {
  cursor: pointer;
}

.checklist-view-title {
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 15px;
  padding: 0px 20px;
  padding-top: 15px;
}

.inspection-badge-container {
  padding: 0px 20px;
}

:deep(.assigned-date-text) {
  font-size: 12px;
  font-weight: 500 !important;
  line-height: 18px;
  color: #344054;
  margin-top: 2px;
  margin-bottom: 0px !important;
  text-align: start !important;
}

:deep(.ex-universal-table-container .table-content) {
  border: none !important;
}

.checklist-container {
  height: 600px !important;
  background-color: white;
}

:deep(.issue-badge-container) {
  line-height: 0px !important;
}
</style>
